import { useEffect, useRef, useState } from 'react';
import FeeSettingsFields from '../components/FeeSettingsFields';
import { globalSettingsService } from '../services/api';

function centsToEurString(cents) {
  const safeCents = Number.isFinite(cents) ? cents : 0;
  return (safeCents / 100).toFixed(2);
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ONBOARDING_NOTIFICATION_EVENTS = [
  {
    key: 'tenantRegistered',
    title: 'Nuovo tenant registrato',
    description: 'Quando il tenant viene creato all’avvio del percorso di onboarding.',
  },
  {
    key: 'tenantPendingPayment',
    title: 'Contratto firmato - in attesa di completamento checkout',
    description: 'Quando entra in CONTRACT_SIGNED_PENDING_PAYMENT dopo la firma, anche con importo zero.',
  },
  {
    key: 'tenantPendingActivation',
    title: 'Tenant in attesa di attivazione manuale',
    description: 'Quando entra in CONTRACT_SIGNED_PENDING_ACTIVATION, indipendentemente dal piano.',
  },
  {
    key: 'tenantActivated',
    title: 'Tenant attivato',
    description: 'Quando entra in ACTIVE tramite pagamento automatico o attivazione manuale.',
  },
];

const SUBSCRIPTION_NOTIFICATION_EVENTS = [
  {
    key: 'tenantPlanUpgraded',
    title: 'Upgrade piano completato',
    description: 'Quando Stripe conferma il nuovo prezzo e SoonTip applica il nuovo piano.',
  },
  {
    key: 'tenantCancellationRequested',
    title: 'Disdetta abbonamento richiesta',
    description: 'Quando il tenant programma la cessazione alla fine del periodo corrente.',
  },
  {
    key: 'tenantCancellationRevoked',
    title: 'Disdetta abbonamento revocata',
    description: 'Quando il tenant annulla una disdetta già programmata.',
  },
  {
    key: 'tenantSubscriptionEnded',
    title: 'Abbonamento terminato',
    description: 'Quando Stripe conferma la cessazione effettiva dell’abbonamento.',
  },
];

const TENANT_NOTIFICATION_TEST_OPTIONS = [
  { value: 'REGISTERED', label: 'Nuovo tenant registrato' },
  { value: 'PENDING_PAYMENT', label: 'Contratto firmato - checkout da completare' },
  { value: 'PENDING_ACTIVATION', label: 'Tenant in attesa di attivazione' },
  { value: 'ACTIVATED', label: 'Tenant attivato' },
  { value: 'PLAN_UPGRADED', label: 'Upgrade piano completato' },
  { value: 'CANCELLATION_REQUESTED', label: 'Disdetta richiesta' },
  { value: 'CANCELLATION_REVOKED', label: 'Disdetta revocata' },
  { value: 'SUBSCRIPTION_ENDED', label: 'Abbonamento terminato' },
];

const GlobalSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [settings, setSettings] = useState(null);

  const [form, setForm] = useState({
    defaultSoontipFeePercent: 0,
    defaultSoontipFeeFixedEur: '0.00',
    workerMinimumPayoutEur: '5.00',
    workerInstantPayoutFeePercent: 0,
    workerInstantPayoutFeeFixedEur: '0.00'
  });

  // Alert emails state
  const [alertEmails, setAlertEmails] = useState([]);
  const [emailInput, setEmailInput] = useState('');
  const [emailInputError, setEmailInputError] = useState(null);
  const [savingEmails, setSavingEmails] = useState(false);
  const [emailsSuccess, setEmailsSuccess] = useState(null);
  const [emailsError, setEmailsError] = useState(null);
  const emailInputRef = useRef(null);

  // Tenant lifecycle notification state
  const [tenantNotificationRecipients, setTenantNotificationRecipients] = useState([]);
  const [tenantNotificationEvents, setTenantNotificationEvents] = useState({
    tenantRegistered: true,
    tenantPendingPayment: true,
    tenantPendingActivation: true,
    tenantActivated: true,
    tenantPlanUpgraded: true,
    tenantCancellationRequested: true,
    tenantCancellationRevoked: true,
    tenantSubscriptionEnded: true,
  });
  const [tenantEmailInput, setTenantEmailInput] = useState('');
  const [tenantEmailInputError, setTenantEmailInputError] = useState(null);
  const [savingTenantNotifications, setSavingTenantNotifications] = useState(false);
  const [testingTenantNotifications, setTestingTenantNotifications] = useState(false);
  const [tenantNotificationTestEvent, setTenantNotificationTestEvent] = useState('PLAN_UPGRADED');
  const [tenantNotificationsSuccess, setTenantNotificationsSuccess] = useState(null);
  const [tenantNotificationsError, setTenantNotificationsError] = useState(null);
  const tenantEmailInputRef = useRef(null);

  // Auto-activate tenant on payment state
  const [autoActivateEnabled, setAutoActivateEnabled] = useState(true);
  const [autoActivateSaving, setAutoActivateSaving] = useState(false);
  const [autoActivateError, setAutoActivateError] = useState(null);
  const [autoActivateSuccess, setAutoActivateSuccess] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);

      const [data, emailData, tenantNotificationData] = await Promise.all([
        globalSettingsService.getGlobalSettings(),
        globalSettingsService.getAlertEmails(),
        globalSettingsService.getTenantNotificationSettings(),
      ]);
      setSettings(data);

      setForm({
        defaultSoontipFeePercent: data?.defaultSoontipFeePercent ?? 0,
        defaultSoontipFeeFixedEur: centsToEurString(data?.defaultSoontipFeeFixedCents),
        workerMinimumPayoutEur: centsToEurString(data?.workerMinimumPayoutCents ?? 500),
        workerInstantPayoutFeePercent: (data?.workerInstantPayoutFeeBps ?? 0) / 100,
        workerInstantPayoutFeeFixedEur: centsToEurString(data?.workerInstantPayoutFeeFixedCents ?? 0)
      });

      setAlertEmails(emailData?.alertEmails ?? []);
      setTenantNotificationRecipients(tenantNotificationData?.recipients ?? []);
      setTenantNotificationEvents({
        tenantRegistered: tenantNotificationData?.events?.tenantRegistered ?? true,
        tenantPendingPayment: tenantNotificationData?.events?.tenantPendingPayment ?? true,
        tenantPendingActivation: tenantNotificationData?.events?.tenantPendingActivation ?? true,
        tenantActivated: tenantNotificationData?.events?.tenantActivated ?? true,
        tenantPlanUpgraded: tenantNotificationData?.events?.tenantPlanUpgraded ?? true,
        tenantCancellationRequested: tenantNotificationData?.events?.tenantCancellationRequested ?? true,
        tenantCancellationRevoked: tenantNotificationData?.events?.tenantCancellationRevoked ?? true,
        tenantSubscriptionEnded: tenantNotificationData?.events?.tenantSubscriptionEnded ?? true,
      });
      setAutoActivateEnabled(data?.autoActivateTenantOnPayment ?? true);
    } catch (err) {
      console.error('❌ Failed to load global settings:', err);
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      const updated = await globalSettingsService.updateGlobalSettings({
        defaultSoontipFeePercent: form.defaultSoontipFeePercent,
        defaultSoontipFeeFixedEur: form.defaultSoontipFeeFixedEur,
        workerMinimumPayoutEur: form.workerMinimumPayoutEur,
        workerInstantPayoutFeePercent: form.workerInstantPayoutFeePercent,
        workerInstantPayoutFeeFixedEur: form.workerInstantPayoutFeeFixedEur
      });

      setSettings(updated);
      setForm({
        defaultSoontipFeePercent: updated?.defaultSoontipFeePercent ?? form.defaultSoontipFeePercent,
        defaultSoontipFeeFixedEur: centsToEurString(updated?.defaultSoontipFeeFixedCents),
        workerMinimumPayoutEur: centsToEurString(updated?.workerMinimumPayoutCents ?? 500),
        workerInstantPayoutFeePercent: (updated?.workerInstantPayoutFeeBps ?? 0) / 100,
        workerInstantPayoutFeeFixedEur: centsToEurString(updated?.workerInstantPayoutFeeFixedCents ?? 0)
      });

      setSuccessMessage('✅ Impostazioni salvate correttamente');
    } catch (err) {
      console.error('❌ Failed to save global settings:', err);
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const addEmail = () => {
    const trimmed = emailInput.trim().toLowerCase();
    if (!trimmed) return;
    if (!EMAIL_RE.test(trimmed)) {
      setEmailInputError('Indirizzo email non valido');
      return;
    }
    if (alertEmails.includes(trimmed)) {
      setEmailInputError('Indirizzo già presente');
      return;
    }
    setEmailInputError(null);
    setAlertEmails((prev) => [...prev, trimmed]);
    setEmailInput('');
    setEmailsSuccess(null);
    emailInputRef.current?.focus();
  };

  const removeEmail = (email) => {
    setAlertEmails((prev) => prev.filter((e) => e !== email));
    setEmailsSuccess(null);
  };

  const saveEmails = async () => {
    try {
      setSavingEmails(true);
      setEmailsError(null);
      setEmailsSuccess(null);
      const data = await globalSettingsService.updateAlertEmails(alertEmails);
      setAlertEmails(data.alertEmails ?? alertEmails);
      setEmailsSuccess('✅ Indirizzi email salvati');
    } catch (err) {
      console.error('❌ Failed to save alert emails:', err);
      setEmailsError(err.response?.data?.error || err.message);
    } finally {
      setSavingEmails(false);
    }
  };

  const addTenantNotificationEmail = () => {
    const trimmed = tenantEmailInput.trim().toLowerCase();
    if (!trimmed) return;
    if (!EMAIL_RE.test(trimmed)) {
      setTenantEmailInputError('Indirizzo email non valido');
      return;
    }
    if (tenantNotificationRecipients.includes(trimmed)) {
      setTenantEmailInputError('Indirizzo già presente');
      return;
    }

    setTenantNotificationRecipients((previous) => [...previous, trimmed]);
    setTenantEmailInput('');
    setTenantEmailInputError(null);
    setTenantNotificationsSuccess(null);
    tenantEmailInputRef.current?.focus();
  };

  const removeTenantNotificationEmail = (email) => {
    setTenantNotificationRecipients((previous) => previous.filter((item) => item !== email));
    setTenantNotificationsSuccess(null);
  };

  const saveTenantNotifications = async () => {
    try {
      setSavingTenantNotifications(true);
      setTenantNotificationsError(null);
      setTenantNotificationsSuccess(null);
      const data = await globalSettingsService.updateTenantNotificationSettings(
        tenantNotificationRecipients,
        tenantNotificationEvents
      );
      setTenantNotificationRecipients(data.recipients ?? []);
      setTenantNotificationEvents(data.events ?? tenantNotificationEvents);
      setTenantNotificationsSuccess('✅ Configurazione notifiche tenant salvata');
    } catch (err) {
      setTenantNotificationsError(err.response?.data?.error || err.message);
    } finally {
      setSavingTenantNotifications(false);
    }
  };

  const sendTenantNotificationTest = async () => {
    try {
      setTestingTenantNotifications(true);
      setTenantNotificationsError(null);
      setTenantNotificationsSuccess(null);
      const result = await globalSettingsService.sendTenantNotificationTest(tenantNotificationTestEvent);
      setTenantNotificationsSuccess(
        `✅ Email di prova inviata a ${result.sent} ${result.sent === 1 ? 'destinatario' : 'destinatari'}`
      );
    } catch (err) {
      setTenantNotificationsError(err.response?.data?.error || err.message);
    } finally {
      setTestingTenantNotifications(false);
    }
  };

  useEffect(() => {
    if (settings) {
      setAutoActivateEnabled(settings.autoActivateTenantOnPayment ?? true);
    }
  }, [settings]);

  const toggleAutoActivate = async (enabled) => {
    try {
      setAutoActivateSaving(true);
      setAutoActivateError(null);
      setAutoActivateSuccess(null);

      const data = enabled
        ? await globalSettingsService.enableAutoActivateTenant()
        : await globalSettingsService.disableAutoActivateTenant();

      setAutoActivateEnabled(data.autoActivateTenantOnPayment);
      setSettings(prev => ({ ...prev, autoActivateTenantOnPayment: data.autoActivateTenantOnPayment }));
      setAutoActivateSuccess(enabled
        ? '✅ Auto-attivazione abilitata: il tenant verrà attivato automaticamente al pagamento'
        : '✅ Auto-attivazione disabilitata: richiede approvazione manuale dopo il pagamento');
    } catch (err) {
      setAutoActivateError(err.response?.data?.error || err.message);
    } finally {
      setAutoActivateSaving(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div>
      <div className="card">
        <h2>⚙️ Global Settings</h2>
        <p>Default commissione SoonTip per nuove venues e fallback globale.</p>
      </div>

      <div className="card">
        <h3>💸 Default Commissione SoonTip</h3>

        {loading ? (
          <div className="loading">Caricamento impostazioni...</div>
        ) : error ? (
          <div className="error">❌ {error}</div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12, maxWidth: 520 }}>
              <FeeSettingsFields
                percent={form.defaultSoontipFeePercent}
                onPercentChange={(value) =>
                  setForm((prev) => ({
                    ...prev,
                    defaultSoontipFeePercent: value
                  }))
                }
                fixedEur={form.defaultSoontipFeeFixedEur}
                onFixedEurChange={(value) =>
                  setForm((prev) => ({
                    ...prev,
                    defaultSoontipFeeFixedEur: value
                  }))
                }
                onDirty={() => setSuccessMessage(null)}
                fixedInputClassName="form-input"
                fixedInputStyle={{ padding: 10 }}
              />

              <div>
                <button className="btn btn-primary" onClick={save} disabled={saving}>
                  {saving ? 'Salvataggio...' : 'Salva'}
                </button>
              </div>

              {successMessage && (
                <div
                  style={{
                    background: '#e8fff1',
                    border: '1px solid #b7f5cc',
                    color: '#0a5a2a',
                    padding: '10px 12px',
                    borderRadius: 8
                  }}
                >
                  {successMessage}
                </div>
              )}

              <div style={{ fontSize: 12, opacity: 0.8 }}>
                <div>
                  <strong>Ultimo aggiornamento:</strong>{' '}
                  {settings?.updatedAt ? new Date(settings.updatedAt).toLocaleString() : '-'}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="card">
        <h3>Prelievi istantanei worker</h3>
        <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
          Saldo lordo istantaneamente disponibile minimo richiesto per consentire il prelievo. La commissione Stripe viene sottratta dopo il controllo della soglia.
        </p>

        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 12px', marginBottom: 16, fontSize: 13, color: '#1e3a8a' }}>
          Percentuale e quota fissa devono coincidere esattamente con la regola configurata nel{' '}
          <a
            href="https://dashboard.stripe.com/settings/connect/platform_pricing/instant_payouts"
            target="_blank"
            rel="noreferrer"
            style={{ color: '#1d4ed8', fontWeight: 700, textDecoration: 'underline' }}
          >
            Stripe Platform Pricing Tool
          </a>.
          Se i valori non coincidono, il prelievo viene comunque eseguito usando il netto calcolato da Stripe e l'anomalia viene segnalata nel Centro di controllo.
        </div>

        {loading ? (
          <div className="loading">Caricamento impostazioni...</div>
        ) : (
          <div style={{ display: 'grid', gap: 12, maxWidth: 520 }}>
            <label htmlFor="worker-minimum-payout" style={{ display: 'grid', gap: 6, fontSize: 13, fontWeight: 600 }}>
              Soglia minima
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span aria-hidden="true" style={{ fontWeight: 700 }}>EUR</span>
                <input
                  id="worker-minimum-payout"
                  type="number"
                  min="0.01"
                  step="0.01"
                  className="form-input"
                  value={form.workerMinimumPayoutEur}
                  onChange={(event) => {
                    setForm((prev) => ({ ...prev, workerMinimumPayoutEur: event.target.value }));
                    setSuccessMessage(null);
                  }}
                  style={{ maxWidth: 180, padding: 10 }}
                />
              </div>
            </label>
            <label htmlFor="worker-instant-payout-percent" style={{ display: 'grid', gap: 6, fontSize: 13, fontWeight: 600 }}>
              Commissione percentuale
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  id="worker-instant-payout-percent"
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  className="form-input"
                  value={form.workerInstantPayoutFeePercent}
                  onChange={(event) => {
                    setForm((prev) => ({ ...prev, workerInstantPayoutFeePercent: event.target.value }));
                    setSuccessMessage(null);
                  }}
                  style={{ maxWidth: 180, padding: 10 }}
                />
                <span aria-hidden="true" style={{ fontWeight: 700 }}>%</span>
              </div>
            </label>
            <label htmlFor="worker-instant-payout-fixed" style={{ display: 'grid', gap: 6, fontSize: 13, fontWeight: 600 }}>
              Commissione fissa
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span aria-hidden="true" style={{ fontWeight: 700 }}>EUR</span>
                <input
                  id="worker-instant-payout-fixed"
                  type="number"
                  min="0"
                  step="0.01"
                  className="form-input"
                  value={form.workerInstantPayoutFeeFixedEur}
                  onChange={(event) => {
                    setForm((prev) => ({ ...prev, workerInstantPayoutFeeFixedEur: event.target.value }));
                    setSuccessMessage(null);
                  }}
                  style={{ maxWidth: 180, padding: 10 }}
                />
              </div>
            </label>
            <div style={{ fontSize: 12, color: '#6b7280' }}>
              Imposta una soglia superiore alla commissione fissa configurata nel Platform Pricing Tool di Stripe. I fondi in elaborazione sono esclusi.
            </div>
            <div>
              <button
                className="btn btn-primary"
                onClick={save}
                disabled={saving
                  || !(Number(form.workerMinimumPayoutEur) > 0)
                  || Number(form.workerInstantPayoutFeePercent) < 0
                  || Number(form.workerInstantPayoutFeeFixedEur) < 0}
              >
                {saving ? 'Salvataggio...' : 'Salva impostazioni prelievo'}
              </button>
            </div>
            {successMessage && (
              <div style={{ background: '#e8fff1', border: '1px solid #b7f5cc', color: '#0a5a2a', padding: '10px 12px', borderRadius: 8 }}>
                {successMessage}
              </div>
            )}
            {error && (
              <div role="alert" style={{ background: '#fff1f1', border: '1px solid #fecaca', color: '#b91c1c', padding: '10px 12px', borderRadius: 8 }}>
                {error}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Alert Email Fatture ─────────────────────────────────────────────── */}
      <div className="card">
        <h3>📧 Alert Email Fatture</h3>
        <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
          Questi indirizzi riceveranno una notifica email quando il job mensile o retry
          termina con errori o fallisce completamente.
        </p>

        {loading ? (
          <div className="loading">Caricamento...</div>
        ) : (
          <div style={{ maxWidth: 520 }}>
            {/* Current email list */}
            {alertEmails.length === 0 ? (
              <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 12 }}>
                Nessun indirizzo configurato — gli alert sono disabilitati.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
                {alertEmails.map((email) => (
                  <div
                    key={email}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      background: '#f3f4f6',
                      borderRadius: 6,
                      padding: '6px 10px',
                      fontSize: 13,
                    }}
                  >
                    <span style={{ flex: 1 }}>{email}</span>
                    <button
                      onClick={() => removeEmail(email)}
                      title="Rimuovi"
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#e11d48',
                        fontSize: 16,
                        lineHeight: 1,
                        padding: '0 2px',
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add email row */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <input
                ref={emailInputRef}
                type="email"
                className="form-input"
                placeholder="nuova@email.com"
                value={emailInput}
                onChange={(e) => {
                  setEmailInput(e.target.value);
                  setEmailInputError(null);
                }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEmail(); } }}
                style={{ flex: 1, padding: 8 }}
              />
              <button
                className="btn btn-secondary"
                onClick={addEmail}
                disabled={!emailInput.trim()}
              >
                Aggiungi
              </button>
            </div>

            {emailInputError && (
              <div style={{ fontSize: 12, color: '#e11d48', marginBottom: 8 }}>
                {emailInputError}
              </div>
            )}

            <div>
              <button
                className="btn btn-primary"
                onClick={saveEmails}
                disabled={savingEmails}
              >
                {savingEmails ? 'Salvataggio...' : 'Salva indirizzi'}
              </button>
            </div>

            {emailsSuccess && (
              <div
                style={{
                  marginTop: 10,
                  background: '#e8fff1',
                  border: '1px solid #b7f5cc',
                  color: '#0a5a2a',
                  padding: '8px 12px',
                  borderRadius: 8,
                  fontSize: 13,
                }}
              >
                {emailsSuccess}
              </div>
            )}

            {emailsError && (
              <div
                style={{
                  marginTop: 10,
                  background: '#fff1f1',
                  border: '1px solid #fecaca',
                  color: '#b91c1c',
                  padding: '8px 12px',
                  borderRadius: 8,
                  fontSize: 13,
                }}
              >
                ❌ {emailsError}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Tenant Lifecycle Notifications ─────────────────────────────── */}
      <div className="card">
        <h3>📩 Notifiche ciclo di vita tenant</h3>
        <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
          Configura gli indirizzi che ricevono notifiche quando un tenant raggiunge
          una fase rilevante dell&apos;onboarding. Queste impostazioni sono separate dagli alert fatture.
        </p>

        {loading ? (
          <div className="loading">Caricamento...</div>
        ) : (
          <div style={{ maxWidth: 620 }}>
            <h4 style={{ marginBottom: 10 }}>Destinatari</h4>

            {tenantNotificationRecipients.length === 0 ? (
              <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 12 }}>
                Nessun destinatario configurato — le notifiche tenant sono disabilitate.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
                {tenantNotificationRecipients.map((email) => (
                  <div
                    key={email}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      background: '#f3f4f6',
                      borderRadius: 6,
                      padding: '6px 10px',
                      fontSize: 13,
                    }}
                  >
                    <span style={{ flex: 1 }}>{email}</span>
                    <button
                      type="button"
                      onClick={() => removeTenantNotificationEmail(email)}
                      title="Rimuovi"
                      aria-label={`Rimuovi ${email}`}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#e11d48',
                        fontSize: 16,
                        lineHeight: 1,
                        padding: '0 2px',
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <input
                ref={tenantEmailInputRef}
                type="email"
                className="form-input"
                placeholder="nuova@email.com"
                value={tenantEmailInput}
                onChange={(event) => {
                  setTenantEmailInput(event.target.value);
                  setTenantEmailInputError(null);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    addTenantNotificationEmail();
                  }
                }}
                style={{ flex: 1, padding: 8 }}
              />
              <button
                type="button"
                className="btn btn-secondary"
                onClick={addTenantNotificationEmail}
                disabled={!tenantEmailInput.trim()}
              >
                Aggiungi
              </button>
            </div>

            {tenantEmailInputError && (
              <div style={{ fontSize: 12, color: '#e11d48', marginBottom: 12 }}>
                {tenantEmailInputError}
              </div>
            )}

            <h4 style={{ margin: '22px 0 10px' }}>Eventi di onboarding</h4>
            <div style={{ display: 'grid', gap: 10 }}>
              {ONBOARDING_NOTIFICATION_EVENTS.map((item) => (
                <label
                  key={item.key}
                  style={{
                    display: 'flex',
                    gap: 10,
                    alignItems: 'flex-start',
                    padding: 12,
                    border: '1px solid #e5e7eb',
                    borderRadius: 8,
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={tenantNotificationEvents[item.key]}
                    onChange={(event) => {
                      setTenantNotificationEvents((previous) => ({
                        ...previous,
                        [item.key]: event.target.checked,
                      }));
                      setTenantNotificationsSuccess(null);
                    }}
                    style={{ width: 18, height: 18, marginTop: 1 }}
                  />
                  <span>
                    <strong style={{ display: 'block', fontSize: 14 }}>{item.title}</strong>
                    <span style={{ display: 'block', color: '#6b7280', fontSize: 12, marginTop: 3 }}>
                      {item.description}
                    </span>
                  </span>
                </label>
              ))}
            </div>

            <h4 style={{ margin: '22px 0 10px' }}>Eventi piano e abbonamento</h4>
            <div style={{ display: 'grid', gap: 10 }}>
              {SUBSCRIPTION_NOTIFICATION_EVENTS.map((item) => (
                <label
                  key={item.key}
                  style={{
                    display: 'flex',
                    gap: 10,
                    alignItems: 'flex-start',
                    padding: 12,
                    border: '1px solid #e5e7eb',
                    borderRadius: 8,
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={tenantNotificationEvents[item.key]}
                    onChange={(event) => {
                      setTenantNotificationEvents((previous) => ({
                        ...previous,
                        [item.key]: event.target.checked,
                      }));
                      setTenantNotificationsSuccess(null);
                    }}
                    style={{ width: 18, height: 18, marginTop: 1 }}
                  />
                  <span>
                    <strong style={{ display: 'block', fontSize: 14 }}>{item.title}</strong>
                    <span style={{ display: 'block', color: '#6b7280', fontSize: 12, marginTop: 3 }}>
                      {item.description}
                    </span>
                  </span>
                </label>
              ))}
            </div>

            <div style={{ marginTop: 20 }}>
              <label htmlFor="tenant-notification-test-event" style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                Template email di prova
              </label>
              <select
                id="tenant-notification-test-event"
                className="form-input"
                value={tenantNotificationTestEvent}
                onChange={(event) => setTenantNotificationTestEvent(event.target.value)}
                style={{ width: '100%', padding: 8 }}
              >
                {TENANT_NOTIFICATION_TEST_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 18 }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={sendTenantNotificationTest}
                disabled={testingTenantNotifications || tenantNotificationRecipients.length === 0}
                title="Usa la configurazione già salvata"
              >
                {testingTenantNotifications ? 'Invio in corso...' : 'Invia email di prova'}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={saveTenantNotifications}
                disabled={savingTenantNotifications}
              >
                {savingTenantNotifications ? 'Salvataggio...' : 'Salva configurazione'}
              </button>
            </div>
            <div style={{ color: '#6b7280', fontSize: 11, marginTop: 6 }}>
              Salva la configurazione prima di inviare la prova.
            </div>

            {tenantNotificationsSuccess && (
              <div style={{ marginTop: 12, background: '#e8fff1', border: '1px solid #b7f5cc', color: '#0a5a2a', padding: '8px 12px', borderRadius: 8, fontSize: 13 }}>
                {tenantNotificationsSuccess}
              </div>
            )}
            {tenantNotificationsError && (
              <div role="alert" style={{ marginTop: 12, background: '#fff1f1', border: '1px solid #fecaca', color: '#b91c1c', padding: '8px 12px', borderRadius: 8, fontSize: 13 }}>
                ❌ {tenantNotificationsError}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Auto-Activate Tenant on Payment ──────────────────────────────── */}
      <div className="card">
        <h3>🚀 Auto-Attivazione Tenant al Pagamento</h3>

        <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13 }}>
          <strong>⚠️ Questa impostazione vale solo per i piani a pagamento.</strong><br />
          I piani <strong>free (€0)</strong> richiedono <em>sempre</em> attivazione manuale da parte di SoonTip,
          indipendentemente da questa impostazione.
        </div>

        <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
          Quando abilitato, il tenant con un piano a pagamento viene attivato automaticamente (<strong>ACTIVE</strong>)
          non appena il primo pagamento Stripe viene confermato, senza richiedere approvazione manuale.
          Il metodo di attivazione viene tracciato nel tracker (<em>AUTO_PAYMENT</em> oppure <em>MANUAL</em>).
        </p>

        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={autoActivateEnabled}
              onChange={(e) => toggleAutoActivate(e.target.checked)}
              disabled={autoActivateSaving}
              style={{ width: 18, height: 18 }}
            />
            <span>{autoActivateEnabled ? '🟢 Abilitato' : '🔴 Disabilitato'}</span>
          </label>
          {autoActivateSaving && <span style={{ fontSize: 12, color: '#666' }}>Salvataggio...</span>}
        </div>

        {autoActivateError && (
          <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b', padding: '10px 12px', borderRadius: 8, marginBottom: 12 }}>
            ❌ {autoActivateError}
          </div>
        )}
        {autoActivateSuccess && (
          <div style={{ background: '#e8fff1', border: '1px solid #b7f5cc', color: '#0a5a2a', padding: '10px 12px', borderRadius: 8, marginBottom: 12 }}>
            {autoActivateSuccess}
          </div>
        )}

        <div style={{ fontSize: 12, background: '#f3f4f6', padding: 12, borderRadius: 8, lineHeight: 1.7 }}>
          <strong>📖 Comportamento per tipo di piano:</strong>
          <table style={{ marginTop: 10, borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr style={{ background: '#e5e7eb' }}>
                <th style={{ padding: '6px 10px', textAlign: 'left', border: '1px solid #d1d5db' }}>Tipo piano</th>
                <th style={{ padding: '6px 10px', textAlign: 'left', border: '1px solid #d1d5db' }}>Questa impostazione</th>
                <th style={{ padding: '6px 10px', textAlign: 'left', border: '1px solid #d1d5db' }}>Cosa succede dopo il pagamento</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: '6px 10px', border: '1px solid #d1d5db' }}>💳 Piano a pagamento</td>
                <td style={{ padding: '6px 10px', border: '1px solid #d1d5db' }}>🟢 Abilitato</td>
                <td style={{ padding: '6px 10px', border: '1px solid #d1d5db' }}>✅ Attivazione automatica → <code>ACTIVE</code> (<em>activationSource: AUTO_PAYMENT</em>)</td>
              </tr>
              <tr style={{ background: '#f9fafb' }}>
                <td style={{ padding: '6px 10px', border: '1px solid #d1d5db' }}>💳 Piano a pagamento</td>
                <td style={{ padding: '6px 10px', border: '1px solid #d1d5db' }}>🔴 Disabilitato</td>
                <td style={{ padding: '6px 10px', border: '1px solid #d1d5db' }}>⏳ In attesa approvazione SoonTip → <code>CONTRACT_SIGNED_PENDING_ACTIVATION</code></td>
              </tr>
              <tr>
                <td style={{ padding: '6px 10px', border: '1px solid #d1d5db' }}>🆓 Piano free (€0)</td>
                <td style={{ padding: '6px 10px', border: '1px solid #d1d5db' }}>🟢 o 🔴 (ignorato)</td>
                <td style={{ padding: '6px 10px', border: '1px solid #d1d5db' }}>⏳ Sempre in attesa approvazione manuale SoonTip → <code>CONTRACT_SIGNED_PENDING_ACTIVATION</code></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default GlobalSettings;
